/**
 * HTML Sanitizer Utility
 * 
 * Provides secure HTML sanitization for user-generated or legacy content using DOMPurify.
 * Configured to allow safe formatting tags and trusted iframe embeds from whitelisted domains.
 * 
 * Usage:
 *   import { sanitizeHTML } from '@/shared/utils/html-sanitizer';
 *   
 *   <div dangerouslySetInnerHTML={{ __html: sanitizeHTML(content) }} />
 */

import DOMPurify from 'dompurify';

/**
 * HTML tags allowed in sanitized content.
 * 
 * Includes:
 * - Headings: h1-h6
 * - Text formatting: p, br, strong, em, u
 * - Links: a
 * - Lists: ul, ol, li
 * - Code: code, pre, blockquote
 * - Media embeds: iframe (restricted to trusted domains)
 */
const ALLOWED_TAGS = [
  'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
  'p', 'br', 'strong', 'em', 'u', 'a',
  'ul', 'ol', 'li',
  'blockquote', 'code', 'pre',
  'iframe',
  'div', 'span', // Allow for structure
  'img', // Allow images
];

/**
 * HTML attributes allowed in sanitized content.
 * 
 * Includes attributes for:
 * - Links: href, target, rel
 * - Iframes: src, width, height, frameborder, allowfullscreen, allow
 * - Images: src, alt, width, height
 * - Styling: class, style (limited)
 */
const ALLOWED_ATTR = [
  'href', 'target', 'rel',
  'src', 'width', 'height', 'frameborder', 'allowfullscreen', 'allow',
  'alt', 'title', 'class', 'style', // Allow style for responsive embeds
];

/**
 * Trusted iframe domains for embedded content.
 * 
 * Only iframes from these domains will be allowed.
 * Prevents XSS attacks via malicious iframe sources.
 * 
 * Supported platforms:
 * - YouTube (standard and privacy-enhanced)
 * - Vimeo
 * - Spotify
 * - SoundCloud
 */
const TRUSTED_IFRAME_DOMAINS = [
  'www.youtube.com',
  'youtube.com',
  'www.youtube-nocookie.com',
  'player.vimeo.com',
  'vimeo.com',
  'open.spotify.com',
  'podcasters.spotify.com',
  'play.libsyn.com',
  'w.soundcloud.com',
  'embed.music.apple.com',
  'embed.podcasts.apple.com',
];

function isTrustedIframeSrc(src: string): boolean {
  try {
    const normalizedSrc = src.startsWith('//') ? `https:${src}` : src;
    const url = new URL(normalizedSrc);
    return TRUSTED_IFRAME_DOMAINS.includes(url.hostname);
  } catch {
    return false;
  }
}

/**
 * Sanitizes HTML content to prevent XSS attacks while preserving safe formatting.
 * 
 * Security features:
 * - Removes potentially dangerous tags and attributes
 * - Allows only trusted iframe sources (YouTube, Vimeo, Spotify, SoundCloud)
 * - Blocks data attributes (prevents data exfiltration)
 * - Enforces HTTPS for external resources
 * 
 * @param dirty - The raw HTML string to sanitize
 * @returns Sanitized HTML string safe for rendering with dangerouslySetInnerHTML
 * 
 * @example
 * const userContent = '<p>Hello <script>alert("xss")</script></p>';
 * const safe = sanitizeHTML(userContent);
 * // returns '<p>Hello </p>' (script removed)
 * 
 * @example
 * const embedContent = '<iframe src="https://www.youtube.com/embed/abc123"></iframe>';
 * const safe = sanitizeHTML(embedContent);
 * // returns the iframe (trusted domain)
 * 
 * @example
 * const maliciousEmbed = '<iframe src="https://evil.com/steal"></iframe>';
 * const safe = sanitizeHTML(maliciousEmbed);
 * // returns '' (untrusted domain removed)
 */
export function sanitizeHTML(dirty: string): string {
  if (!dirty || typeof dirty !== 'string') {
    return '';
  }
  
  DOMPurify.addHook('afterSanitizeAttributes', (node: Element) => {
    if (node.tagName === 'A' && node.hasAttribute('href')) {
      const href = node.getAttribute('href') || '';
      if (href.startsWith('http')) {
        node.setAttribute('target', '_blank');
        node.setAttribute('rel', 'noopener noreferrer');
      }
    }

    if (node.tagName === 'IFRAME') {
      const src = node.getAttribute('src') || '';
      if (!src || !isTrustedIframeSrc(src)) {
        node.parentNode?.removeChild(node);
        return;
      }
      node.setAttribute(
        'sandbox',
        'allow-scripts allow-same-origin allow-presentation allow-popups allow-popups-to-escape-sandbox',
      );
    }
  });
  
  const sanitized = DOMPurify.sanitize(dirty, {
    ALLOWED_TAGS,
    ALLOWED_ATTR,
    ALLOW_DATA_ATTR: false,
    ADD_TAGS: ['iframe'],
    ADD_ATTR: ['frameborder', 'allowfullscreen', 'allow', 'style'],
    ALLOW_UNKNOWN_PROTOCOLS: false,
    WHOLE_DOCUMENT: false,
  });
  
  DOMPurify.removeHook('afterSanitizeAttributes');
  
  return sanitized;
}

/**
 * Strips all HTML tags from content, leaving only plain text.
 * 
 * Useful for generating text excerpts or meta descriptions from HTML content.
 * 
 * @param html - The HTML string to strip
 * @returns Plain text with all HTML tags removed
 * 
 * @example
 * stripHTML('<p>Hello <strong>world</strong></p>') // returns 'Hello world'
 * stripHTML('<h1>Title</h1><p>Content</p>') // returns 'Title Content'
 */
export function stripHTML(html: string): string {
  if (!html || typeof html !== 'string') {
    return '';
  }
  
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: [], // No tags allowed
    KEEP_CONTENT: true, // Keep text content
  });
}

/**
 * Truncates HTML content to a maximum length while preserving complete tags.
 * 
 * Useful for generating previews or excerpts from HTML content.
 * Ensures HTML remains valid after truncation by closing any open tags.
 * 
 * @param html - The HTML string to truncate
 * @param maxLength - Maximum length of plain text (default: 200 characters)
 * @param suffix - Suffix to append if truncated (default: '...')
 * @returns Truncated and sanitized HTML
 * 
 * @example
 * truncateHTML('<p>This is a very long paragraph that needs truncation</p>', 20)
 * // returns '<p>This is a very long...</p>'
 */
export function truncateHTML(
  html: string,
  maxLength: number = 200,
  suffix: string = '...',
): string {
  if (!html || typeof html !== 'string') {
    return '';
  }
  
  // First, strip HTML to get plain text length
  const plainText = stripHTML(html);
  
  // If already short enough, return sanitized version
  if (plainText.length <= maxLength) {
    return sanitizeHTML(html);
  }
  
  // Truncate plain text
  const truncatedText = plainText.substring(0, maxLength - suffix.length) + suffix;
  
  // Wrap in paragraph tag and sanitize
  return sanitizeHTML(`<p>${truncatedText}</p>`);
}

